import { Box, Typography, Paper, Checkbox, FormControlLabel, Chip, Collapse, IconButton } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Move, MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_STATUS } from '../../../move/move';
import { AttackPokemonData } from '../../../types/attack';
import { PokemonV2 } from '../../../pokemon/pokemon';
import { retCalc } from '../../../types/damage';
import type { otherSetting } from '../../../types/other-setting';
import DamageResultDetails from '../../../components/DamageResultDetails';
import {
  DamageSettingHeaderControls,
  DamageSettingPowerSelect,
  buildDamageSettingSummaryTags,
  resolveDamageSettingVisibility,
  resolveMoveDamageSettingWithDefaults,
} from '../../../components/DamageSettingControls';
import { buildCombinedDamageResultWithHitCounts, resolveMultiHitCount } from '../../../lib/damage/combined';
import type { MoveDamageSetting } from '../../../types/damage-setting';
import { resolveOffenceMoveType } from '../../../lib/move-type';
import { POKEMON_TYPE_ELECTRIC } from '../../../types/type';
import { storageClient } from '../../../storage';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AttackModeDisplayProps {
  leftSidePokemon: PokemonV2;
  rightSideTargetPokemon: AttackPokemonData;
  otherSetting: otherSetting;
  calculateAttackModeDamage: (
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ) => string[];
  getAttackModeDamageDetail: (
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ) => retCalc[];
  getMoveDisplayName: (move: any, pokemonId: number) => string;
  getMoveDisplayPower: (move: any, pokemonId: number) => number | string;
  enableSum?: boolean;
  defenderHp?: number;
}

const COMBINED_MOVE: Move = {
  id: -1,
  name_ja: '合算結果',
  name_en: 'Combined',
  type: 1,
  accuracy: 0,
  power: 0,
  damage_class_number: MOVE_DAMAGE_CLASS_PHYSICAL,
  min_renzoku: 0,
  max_renzoku: 0,
};

const AttackModeDisplay = memo(function AttackModeDisplay({
  leftSidePokemon,
  rightSideTargetPokemon,
  otherSetting,
  calculateAttackModeDamage,
  getAttackModeDamageDetail,
  getMoveDisplayName,
  getMoveDisplayPower,
  enableSum = false,
  defenderHp = 0,
}: AttackModeDisplayProps) {
  const selectedMoves = (leftSidePokemon.selected_moves || []).filter(
    (move) => move.damage_class_number !== MOVE_DAMAGE_CLASS_STATUS,
  );
  const [selectedMoveIds, setSelectedMoveIds] = useState<number[]>([]);
  const [moveSettingByMoveId, setMoveSettingByMoveId] = useState<Record<number, MoveDamageSetting>>({});
  const [detailOpenByMoveId, setDetailOpenByMoveId] = useState<Record<number, boolean>>({});
  const selectedMoveIdList = useMemo(() => selectedMoves.map((move) => move.id), [selectedMoves]);
  const persistKey = useMemo(
    () => `${leftSidePokemon.id}:${rightSideTargetPokemon.id}`,
    [leftSidePokemon.id, rightSideTargetPokemon.id],
  );
  const hasLoadedPersistedState = useRef(false);
  const loadedPersistKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (loadedPersistKeyRef.current === persistKey) {
      return;
    }
    loadedPersistKeyRef.current = persistKey;
    hasLoadedPersistedState.current = false;

    // 追加設定UIはポケモン本体の入力とは独立しているため、localStorageに別枠で保持する
    const stored = storageClient.pokemon.damageSettingUi.load();
    const entry = stored.success ? stored.data?.attackModeByKey?.[persistKey] : undefined;

    setSelectedMoveIds(entry?.selectedMoveIds ?? []);
    const loadedMoveSettingByMoveId = entry?.moveSettingByMoveId ?? {};
    const legacyJuuden = typeof (entry as any)?.commonSetting?.isJuuden === 'boolean' ? (entry as any).commonSetting.isJuuden : undefined;

    if (legacyJuuden === undefined) {
      setMoveSettingByMoveId(loadedMoveSettingByMoveId);
    } else {
      // 旧スキーマの共通設定（じゅうでん）を技ごとの設定へ移行する
      const next: Record<number, MoveDamageSetting> = { ...loadedMoveSettingByMoveId };
      selectedMoveIdList.forEach((moveId) => {
        const current = next[moveId] ?? {};
        if (current.isJuuden === undefined) {
          next[moveId] = { ...current, isJuuden: legacyJuuden };
        }
      });
      setMoveSettingByMoveId(next);
    }
    hasLoadedPersistedState.current = true;
  }, [persistKey, selectedMoveIdList]);

  const resolveMoveSetting = useCallback(
    (moveId: number) => {
      const move = selectedMoves.find((m) => m.id === moveId);
      if (!move) return undefined;
      return resolveMoveDamageSettingWithDefaults(move, moveSettingByMoveId[moveId]);
    },
    [selectedMoves, moveSettingByMoveId],
  );

  const damageResults = calculateAttackModeDamage(leftSidePokemon, rightSideTargetPokemon, undefined, resolveMoveSetting);
  const damageDetails = getAttackModeDamageDetail(leftSidePokemon, rightSideTargetPokemon, undefined, resolveMoveSetting);

  useEffect(() => {
    setSelectedMoveIds((prev) => prev.filter((moveId) => selectedMoveIdList.includes(moveId)));
  }, [selectedMoveIdList]);

  useEffect(() => {
    // 技が外れたとき、詳細開閉の状態を掃除する
    setDetailOpenByMoveId((prev) => {
      const next: Record<number, boolean> = {};
      selectedMoveIdList.forEach((moveId) => {
        if (prev[moveId]) {
          next[moveId] = prev[moveId];
        }
      });
      return next;
    });
  }, [selectedMoveIdList]);

  useEffect(() => {
    // 技が外れたとき、不要な設定状態を掃除する（結果カード単位の設定なのでキーは moveId だけでよい）
    setMoveSettingByMoveId((prev) => {
      const next: Record<number, MoveDamageSetting> = {};
      selectedMoveIdList.forEach((moveId) => {
        const current = prev[moveId];
        if (current) {
          next[moveId] = current;
        }
      });
      return next;
    });
  }, [selectedMoveIdList]);

  useEffect(() => {
    if (!hasLoadedPersistedState.current) {
      return;
    }
    const result = storageClient.pokemon.damageSettingUi.update((prev) => ({
      ...prev,
      attackModeByKey: {
        ...prev.attackModeByKey,
        [persistKey]: {
          selectedMoveIds,
          moveSettingByMoveId,
        },
      },
    }));
    if (!result.success) {
      console.error('追加設定UIの保存に失敗しました:', result.error);
    }
  }, [moveSettingByMoveId, persistKey, selectedMoveIds]);

  const getBackgroundColor = (damageResult: string): string => {
    if (damageResult === '無効' || damageResult.includes('無効')) {
      return '#f5f5f5';
    } else if (damageResult.includes('確定1発')) {
      return '#ffcdd2';
    } else if (damageResult.includes('乱数1発')) {
      return '#fff3c4';
    }
    return '#c8e6c9';
  };

  const handleToggleMove = (moveId: number) => {
    setSelectedMoveIds((prev) =>
      prev.includes(moveId) ? prev.filter((id) => id !== moveId) : [...prev, moveId],
    );
  };

  const handleUpdateMoveSetting = useCallback((moveId: number, nextSetting: MoveDamageSetting) => {
    setMoveSettingByMoveId((prev) => ({
      ...prev,
      [moveId]: nextSetting,
    }));
  }, []);

  const handleToggleDetail = useCallback((moveId: number) => {
    setDetailOpenByMoveId((prev) => ({
      ...prev,
      [moveId]: !prev[moveId],
    }));
  }, []);

  const combinedDetail = useMemo(() => {
    if (!enableSum || defenderHp <= 0 || selectedMoveIds.length === 0) {
      return null;
    }
    // 合算時は連続技の回数を考慮して乱数分布を再構築する
    const selectedDetails = selectedMoves
      .map((move, index) =>
        selectedMoveIds.includes(move.id)
          ? { detail: damageDetails[index], hitCount: resolveMultiHitCount(move) }
          : null,
      )
      .filter((item): item is { detail: retCalc; hitCount: number } => Boolean(item?.detail));
    return buildCombinedDamageResultWithHitCounts(selectedDetails, defenderHp);
  }, [enableSum, defenderHp, selectedMoveIds, selectedMoves, damageDetails]);

  const combinedBackgroundColor = useMemo(() => {
    if (!combinedDetail || defenderHp <= 0) {
      return '#f5f5f5';
    }
    if (combinedDetail.max_damage < defenderHp) {
      return '#c8e6c9';
    }
    if (combinedDetail.min_damage >= defenderHp) {
      return '#ffcdd2';
    }
    return '#fff3c4';
  }, [combinedDetail, defenderHp]);

  if (selectedMoves.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          左側のポケモンに攻撃技が選択されていません
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {selectedMoves.map((move, index) => {
        const damageResult = damageResults[index] || '無効';
        const damageDetail = damageDetails[index];
        const backgroundColor = getBackgroundColor(damageResult);
        const isChecked = selectedMoveIds.includes(move.id);
        const setting = moveSettingByMoveId[move.id] ?? {};
        const showJuuden = resolveOffenceMoveType(move, leftSidePokemon, otherSetting) === POKEMON_TYPE_ELECTRIC;
        const summaryTags = buildDamageSettingSummaryTags(move, setting, showJuuden);
        if (enableSum && isChecked) {
          summaryTags.push({ key: `sum-${move.id}`, label: '合算' });
        }
        const { hasAnyControl } = resolveDamageSettingVisibility(move, showJuuden);
        const isDetailOpen = Boolean(detailOpenByMoveId[move.id]);

        return (
          <Paper
            key={move.id}
            sx={{
              p: 1.5,
              border: '1px solid #e0e0e0',
              backgroundColor,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DamageResultDetails
                  move={move}
                  pokemonId={leftSidePokemon.id}
                  damageText={damageResult}
                  damageDetail={damageDetail}
                  getMoveDisplayName={getMoveDisplayName}
                  getMoveDisplayPower={getMoveDisplayPower}
                />
                {summaryTags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {summaryTags.map((tag) => (
                      <Chip key={tag.key} label={tag.label} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    ))}
                  </Box>
                )}
                {hasAnyControl && (
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => handleToggleDetail(move.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleToggleDetail(move.id);
                      }
                    }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, cursor: 'pointer', width: 'fit-content' }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      詳細設定
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleDetail(move.id);
                      }}
                      sx={{ padding: '2px' }}
                    >
                      {isDetailOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                )}
                <Collapse in={isDetailOpen} unmountOnExit>
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                    <DamageSettingHeaderControls
                      move={move}
                      setting={setting}
                      onChangeSetting={(next) => handleUpdateMoveSetting(move.id, next)}
                      showJuuden={showJuuden}
                    />
                    <DamageSettingPowerSelect
                      move={move}
                      setting={setting}
                      onChangeSetting={(next) => handleUpdateMoveSetting(move.id, next)}
                    />
                  </Box>
                </Collapse>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                {enableSum ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={() => handleToggleMove(move.id)}
                      />
                    }
                    label="合算"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
                  />
                ) : null}
              </Box>
            </Box>
          </Paper>
        );
      })}
      {enableSum && combinedDetail && (
        <Paper
          sx={{
            p: 1.5,
            border: '1px solid #e0e0e0',
            backgroundColor: combinedBackgroundColor,
          }}
        >
          <DamageResultDetails
            move={COMBINED_MOVE}
            pokemonId={leftSidePokemon.id}
            damageText={combinedDetail.text}
            damageDetail={combinedDetail}
            getMoveDisplayName={getMoveDisplayName}
            getMoveDisplayPower={getMoveDisplayPower}
            customLabel="合算結果"
            hideMoveMeta
          />
        </Paper>
      )}
    </Box>
  );
});

export default AttackModeDisplay;
